import React, { useState, useEffect, useRef, useMemo } from "react";
import { Container } from "react-bootstrap";
import axios from "axios";
import Slider from "react-slick";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../../css/DarkThemeClassic.css";
import "../../css/Fondo.css";
import { API_BASE_URL, CLOUDINARY_CLOUD_NAME } from "../../config/constants";

const Eventos = ({ theme = "dtc" }) => {
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const sliderRef = useRef(null);

    useEffect(() => {
        const fetchEventos = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE_URL}/getEventosActivados`);
                const sortedEventos = response.data.eventos.sort((a, b) => {
                    const dateA = new Date(a.eventDate._seconds * 1000 + a.eventDate._nanoseconds / 1e6);
                    const dateB = new Date(b.eventDate._seconds * 1000 + b.eventDate._nanoseconds / 1e6);
                    return dateA - dateB;
                });
                setEventos(sortedEventos);
                setLoading(false);
            } catch (err) {
                setLoading(false);
            }
        };

        fetchEventos();
    }, []);

    /* Checkea si es dispositivo movil */
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkIfMobile();
        window.addEventListener("resize", checkIfMobile);
        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);

    const convertDate = (timestamp) => {
        const date = new Date(timestamp._seconds * 1000 + timestamp._nanoseconds / 1e6);
        const formatter = new Intl.DateTimeFormat("es-ES", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
        const parts = formatter.formatToParts(date);
        return `${parts.find((p) => p.type === "day").value} ${parts.find((p) => p.type === "month").value.toUpperCase()} ${parts.find((p) => p.type === "hour").value}:${parts.find((p) => p.type === "minute").value}`;
    };

    const getImageUrl = (publicId) => {
        return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/f_auto/${publicId}`;
    };

    const handleTouchStart = () => {
        if (isMobile && sliderRef.current) {
            sliderRef.current.slickPause();
        }
    };

    const handleTouchEnd = () => {
        if (isMobile && sliderRef.current) {
            sliderRef.current.slickPlay();
        }
    };

    const settings = useMemo(() => ({
        dots: true,
        infinite: eventos.length > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        autoplay: eventos.length > 1,
        autoplaySpeed: 5000,
        adaptiveHeight: true,
        pauseOnFocus: true,
        pauseOnDotsHover: true,
    }), [eventos.length]);

    return (
        <Container fluid className="p-0">
            {loading ? (
                <div
                    style={{
                        width: "100%",
                        height: "80vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "1rem"
                    }}
                >
                    <Skeleton
                        className="skeleton-evento"
                        width="100%"
                        style={{ borderRadius: "8px" }}
                        containerClassName="w-100 h-100"
                        baseColor="#fff"
                        highlightColor="#fffaf2"
                    />
                </div>
            ) : (
                <div 
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <Slider {...settings} ref={sliderRef}>
                        {eventos.length > 0 ? (
                            eventos.map((evento) => (
                                <div
                                    key={evento.id}
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <AnimatedBox
                                        theme={theme}
                                        imageUrl={getImageUrl(evento.imagePublicId)}
                                        date={convertDate(evento.eventDate)}
                                        title={evento.nombre}
                                        description={evento.descripcion}
                                    />
                                </div>
                            ))
                        ) : (
                            <AnimatedBox
                                theme={theme}
                                imageUrl={""}
                                date={""}
                                title={"Estate atento a la grilla"}
                                description={""}
                            />
                        )}
                    </Slider>
                </div>
            )}
        </Container>
    );
};

const AnimatedBox = ({ theme, imageUrl, date, title, description }) => {
    return (
        <div
            className={`${theme}-box ${theme}-b1`}
            style={{
                width: "100%",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            <div
                className={`${theme}-img-box`}
                style={{
                    width: "100%",
                    flex: "1 1 auto",
                    overflow: "hidden",
                }}
            >
                <img
                    src={imageUrl || "imagenes/placeholders/showsplaceholder.jpeg"}
                    alt={title || "Evento"}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        borderRadius: "8px 8px 0 0"
                    }}
                />
            </div>
            <div
                className={`${theme}-detail-box`}
                style={{
                    width: "100%",
                    padding: "1.5rem",
                    boxSizing: "border-box",
                    textAlign: "justify",
                    flexShrink: 0,
                }}
            >
                {date && (
                    <div className={`${theme}-img_date`} style={{ textAlign: "center" }}>
                        <h6>{date}</h6>
                    </div>
                )}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        textAlign: "justify",
                        paddingTop: "clamp(1rem, 2vw, 6rem)",
                        width: "100%"
                    }}
                >
                    <h3>{title}</h3>
                    <p>{description || ""}</p>
                </div>
            </div>
        </div>
    );
};

export default Eventos;